import { nameFields } from '../utils/individualName';
import { sameIntakePersonName, sameIntakeValue } from '../utils/clientDocumentIntake';
import { syncLinkedSignatories } from '../utils/linkedScreeningPerson';
import { ownershipKnownNodes, ownershipRowNodeIds } from '../utils/editOwnershipPercentage';
import { ownershipTreeWithMobileNodes } from '../utils/mobileAssistantTree';

const collections = { shareholders: 'Direct shareholder', directShareholders: 'Direct shareholder', ubos: 'Beneficial owner', controllingPersonsNatural: 'Controlling person', signatories: 'Signatory', controllers: 'Signatory' };
const typeOf = row => /entity|company|holding|pic|foundation|trust|legal person/i.test(row.holderType || row.partyType || row.type || row.trusteeKind || '') ? 'entity' : 'individual';
const rowId = (row, index) => String(row._rowId || row._docRef || `index-${index}`);
const PASSIVE_TYPES = /^(PIC|Domiciliary Company \(PIC\)|HoldingPassive|Holding \(Passive\))$/i;
const ACTIVE_TYPES = /^(Non Quoted Operational Company|Operating Company|HoldingActive|Holding \(Active\))$/i;

// A natural person behind a company: beneficial owner when the company is a PIC or passive holding,
// controlling person when it is an operating company or active holding. When the company's type is
// unknown, no role is shown rather than a guess.
function ownerRole(row, ownership) {
  const nodes = ownershipTreeWithMobileNodes(ownership)?.nodes || [];
  const byId = id => nodes.find(n => String(n.ID || n.id || '') === id);
  const shareholders = ownership.shareholders || ownership.directShareholders || [];
  let parentId = String(byId(nodeId(row))?.parentIDs?.[0] || '');
  let kind = '';
  if (parentId && parentId !== 'X0') kind = byId(parentId)?.type || '';
  if (!kind && !parentId) {
    parentId = String(row.relatedShareholderNodeId || '');
    const parent = shareholders.find(s => (row.relatedShareholderRowId && s._rowId === row.relatedShareholderRowId) ||
      (parentId && parentId !== 'X0' && String(s.treeNodeId || '') === parentId));
    if (parent) kind = parent.isPIC || parent.isHoldingPassive || parent.isPicOrPassiveHolding ? 'PIC'
      : parent.isHoldingActive ? 'HoldingActive' : parent.shareholderEntityType || parent.type || '';
  }
  if (!kind && parentId === 'X0') kind = ownership.uboFlags?.pctNotMeaningful ? 'PIC' : 'Operating Company';
  if (PASSIVE_TYPES.test(kind)) return 'Beneficial owner';
  return ACTIVE_TYPES.test(kind) ? 'Controlling person' : '';
}
const nodeId = row => String(row.treeNodeId || row.subjectNodeId || row.subjectTreeNodeId || '');
const asCustomer = (row, type) => ({ ...nameFields(row), type, name: row.name || '',
  dobOrIncorporationDate: row.dateOfBirth || row.dob || row.dobOrIncorporationDate || row.incorporationDate || '',
  nationalityOrIncorporationCountry: type === 'entity' ? row.incorporationCountry || row.countryOfIncorporation || row.countryOfResidence || '' : row.nationality || '',
  country: row.countryOfResidence || row.domicile || row.country || '', address: row.address || row.registeredAddress || '', city: row.city || '', email: row.email || '', phone: row.phone || '', businessActivity: row.businessActivity || '' });

export function kycDocumentTargets(form) {
  const ownership = form.ownership || {};
  const targets = [{ id: 'customer', name: form.customer?.name || 'Customer', type: form.customer?.type || 'individual', role: 'Customer', customer: form.customer || {}, refs: [], partyIds: ['customer'] }];
  const groups = new Map();
  function add(row, path, id, role, partyId) {
    if (!row?.name?.trim()) return;
    const type = typeOf(row);
    const key = nodeId(row) ? `node:${nodeId(row)}` : row._rowId && !['Signatory'].includes(role) ? `owner:${row._rowId}` : id;
    let target = groups.get(key);
    if (!target) {
      target = { id, name: row.name, type, role, customer: asCustomer(row, type), refs: [], partyIds: [], nodeId: nodeId(row) };
      groups.set(key, target); targets.push(target);
    }
    target.refs.push({ path, row });
    if (!target.partyIds.includes(partyId)) target.partyIds.push(partyId);
    if (role && !target.role.split(' · ').includes(role)) target.role = target.role ? `${target.role} · ${role}` : role;
    return target;
  }
  // Each list has a main copy and sometimes a second one; only the main copy defines people.
  const mainOf = { directShareholders: 'shareholders', controllingPersonsNatural: 'ubos' };
  const isSecondCopy = collection => Array.isArray(ownership[mainOf[collection]]);
  // Rows without a node of their own still match the saved diagram's generated node (SH_rev_n /
  // UBO_rev_n), by the same rule that drew it, so the diagram's box is not listed a second time.
  const treeIds = new Set((ownership.assistantTree?.nodes || []).map(n => String(n.ID || n.id || '')));
  const known = ownershipKnownNodes(ownership);
  const rowNodeIds = {
    shareholders: ownershipRowNodeIds(ownership.shareholders || ownership.directShareholders || [], known, 'SH_rev'),
    ubos: ownershipRowNodeIds(ownership.ubos || ownership.controllingPersonsNatural || [], known, 'UBO_rev'),
  };
  const withTreeNode = (collection, row, index) => {
    const list = ['shareholders', 'directShareholders'].includes(collection) ? 'shareholders'
      : ['ubos', 'controllingPersonsNatural'].includes(collection) ? 'ubos' : '';
    const id = list ? rowNodeIds[list][index] : '';
    return id && !nodeId(row) && treeIds.has(id) ? { ...row, treeNodeId: id } : row;
  };
  Object.entries(collections).forEach(([collection, collectionRole]) => (ownership[collection] || []).forEach((rawRow, index) => {
    if (collection === 'controllers' && Array.isArray(ownership.signatories)) return;
    if (isSecondCopy(collection)) {
      // Kept as a ref of the same person so applied details update both copies; a row only
      // left in the second copy (deleted from the table) is not offered.
      const main = mainOf[collection];
      const mainRow = (ownership[main] || []).find((r, i) => rawRow._rowId ? r._rowId === rawRow._rowId : i === index && r.name === rawRow.name);
      const target = mainRow && targets.find(t => t.refs.some(ref => ref.path[1] === main && ref.row._rowId === mainRow._rowId && ref.row.name === mainRow.name));
      if (target) target.refs.push({ path: ['ownership', collection, index], row: rawRow });
      return;
    }
    const row = withTreeNode(collection, rawRow, index);
    // controllingPersonsNatural mirrors ubos: both are kept as refs (so both stay updated) under one role.
    const role = ['ubos', 'controllingPersonsNatural'].includes(collection) ? ownerRole(row, ownership) : collectionRole;
    const link = row._personLink;
    if (collection === 'signatories' && link?.rowId) {
      const target = targets.find(t => t.refs.some(ref => ref.path[1] === link.collection && ref.row._rowId === link.rowId));
      if (target) {
        target.refs.push({ path: ['ownership', collection, index], row });
        target.partyIds.push(`signatory:${row._rowId || row._docRef || row.name}`);
        target.role = target.role ? `${target.role} · Signatory` : 'Signatory'; return;
      }
    }
    const prefix = collection.includes('Shareholder') || collection === 'shareholders' ? 'shareholder' : ['signatories', 'controllers'].includes(collection) ? 'signatory' : 'ubo';
    add(row, ['ownership', collection, index], `${collection}:${rowId(row,index)}`, role, `${prefix}:${row._rowId || row._docRef || row.name}`);
  }));
  (ownership.legalArrangements?.arrangements || []).forEach((arrangement, ai) => {
    (arrangement.parties || []).forEach((row, pi) => add(row, ['ownership','legalArrangements','arrangements',ai,'parties',pi], `arrangement:${rowId(arrangement,ai)}:${rowId(row,pi)}`, `${row.role || 'Party'} of ${arrangement.name || 'trust'}`, `trustee:${arrangement._rowId || ai}:${row._rowId || row.name}`));
  });
  // Diagram boxes with no table row: intermediate companies only. Generated boxes, people,
  // trusts and direct shareholders always have a row, so an unclaimed one is a deleted row.
  const tree = ownership.assistantTree;
  (tree?.source === 'manualEntry' ? [] : tree?.nodes || []).forEach((node,index) => {
    const id = String(node.ID || node.id || '');
    if (!id || id === 'X0' || !node.name) return;
    if (targets.some(target => target.nodeId === id)) return;
    if (/^(SH|UBO|LA)_rev_\d+$/.test(id) || /^(Individual|Trust|Foundation)$/.test(String(node.type || ''))) return;
    if (String(node.parentIDs?.[0] || '') === 'X0' && Array.isArray(ownership.shareholders || ownership.directShareholders)) return;
    add({ ...node, treeNodeId:id }, ['ownership','assistantTree','nodes',index], `tree:${id}`, 'Ownership structure', `tree:${id}`);
  });
  return targets;
}

export function eligibleDocumentTargets(document, targets) {
  const type = document.extraction?.fields?.clientType?.value;
  return targets.filter(target => !type || target.type === type);
}

const nameTokens = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().replace(/['’]/g, '').split(/[^\p{L}\p{N}]+/u).filter(Boolean);
const legalEndings = new Set(['llc', 'ltd', 'limited', 'inc', 'incorporated', 'corp', 'corporation', 'plc', 'llp', 'lp', 'gmbh', 'ag', 'sa', 'sas', 'sarl', 'bv', 'nv', 'co', 'company', 'fze', 'fzco', 'fz', 'pjsc']);
const genericCompanyWords = new Set(['the', 'and', 'of', 'group', 'holding', 'holdings', 'international', 'global', 'general', 'trading', 'trade', 'services', 'solutions', 'digital', 'technology', 'technologies', 'investment', 'investments', ...legalEndings]);
const entityTokens = value => {
  // Preserve dotted legal abbreviations as single tokens, e.g. L.L.C.
  const tokens = nameTokens(String(value || '').replace(/(?:\b[a-z]\.){2,}[a-z]?\.?/gi, abbreviation => abbreviation.replace(/\./g, '')));
  while (tokens.length && legalEndings.has(tokens[tokens.length - 1])) tokens.pop();
  return tokens;
};

// Association is deliberately separate from field equality: a fuller extracted
// name still appears as a choice in the details review, never a silent update.
function matchesDocumentName(extracted, target) {
  if (target.type === 'entity') {
    if (sameIntakeValue(extracted, target.name)) return true;
    const left = entityTokens(extracted); const right = entityTokens(target.name);
    const [shorter, longer] = left.length <= right.length ? [left, right] : [right, left];
    return shorter.some(token => token.length >= 3 && !genericCompanyWords.has(token)) &&
      shorter.every(token => longer.includes(token));
  }
  if (sameIntakePersonName(extracted, target.name)) return true;
  const left = nameTokens(extracted); const right = nameTokens(target.name);
  const [shorter, longer] = left.length <= right.length ? [left, right] : [right, left];
  if (shorter.length < 2 || shorter.some(token => token.length < 3)) return false;
  const remaining = [...longer];
  const unmatched = shorter.filter(token => {
    const index = remaining.indexOf(token);
    if (index < 0) return true;
    remaining.splice(index, 1);
    return false;
  });
  // Extra middle names / reversed order, or one abbreviation alongside an exact
  // token (Fab Richard / Fabien Richard). A surname alone is not sufficient.
  return unmatched.length === 0 || (unmatched.length === 1 && shorter.length > unmatched.length &&
    remaining.some(token => token.length >= 3 &&
      (token.startsWith(unmatched[0]) || unmatched[0].startsWith(token))));
}

/** Whether the name read on the document fits this person or entity (used to highlight likely holders). */
export function documentNameMatchesTarget(document, target) {
  const name = document.extraction?.fields?.clientName?.value;
  return !!name && matchesDocumentName(name, target);
}

export function matchDocumentTarget(document, targets) {
  const fields = document.extraction?.fields || {};
  if (document.extraction?.multipleSubjects || !fields.clientName?.value || fields.clientName.uncertain || fields.clientName.confidence < .85) return null;
  const matches = eligibleDocumentTargets(document, targets).filter(target => matchesDocumentName(fields.clientName.value, target));
  // Even an exact name is ambiguous if another person/entity also fits it.
  if (matches.length !== 1) return null;
  // Existing KYC details may be precisely what the upload needs to correct.
  // Compare differing dates in the review modal rather than rejecting a unique
  // name association. No document or field is saved until the user applies it.
  return matches[0].id;
}
export function documentTargetForm(form, target) {
  return { ...form, customer: target.customer, ...(target.id === 'customer' ? {} : { sourceOfFundsWealth: {}, intakeSubjectOnly:true }) };
}

export function applyDocumentTargetValues(form, targetId, values) {
  const target = kycDocumentTargets(form).find(t => t.id === targetId);
  if (!target) throw new Error('This person or entity is no longer in the KYC. Please select the document holder again.');
  const next = { ...form };
  if (target.id === 'customer') {
    const customer = { ...form.customer };
    const fields = { clientName:'name', givenNames:'givenNames', familyName:'familyName', address:'address', city:'city', country:'country', nationality:'nationalityOrIncorporationCountry', incorporationCountry:'nationalityOrIncorporationCountry', contactEmail:'email', contactPhone:'phone', businessActivity:'businessActivity' };
    Object.entries(fields).forEach(([from,to]) => { if (values[from] !== undefined) customer[to] = values[from]; });
    const date = target.type === 'entity' ? values.incorporationDate : values.dateOfBirth;
    if (date) customer.dobOrIncorporationDate = new Date(`${date}T00:00:00`);
    next.customer = customer;
    const allowedWealth = ['employment', 'self_employed', 'business_owner'];
    const wealth = Object.keys(values).filter(key => key.startsWith('wealth:')).map(key => key.slice(7)).filter(key => allowedWealth.includes(key));
    if (wealth.length) next.sourceOfFundsWealth = { ...form.sourceOfFundsWealth, sourceOfWealth: [...new Set([...(form.sourceOfFundsWealth?.sourceOfWealth || []), ...wealth])] };
  } else {
    const changes = {};
    const fields = { clientName:'name', givenNames:'givenNames', familyName:'familyName', address:'address', city:'city', country:'countryOfResidence', nationality:'nationality', incorporationCountry:'incorporationCountry', contactEmail:'email', contactPhone:'phone', businessActivity:'businessActivity', dateOfBirth:'dateOfBirth', incorporationDate:'incorporationDate' };
    Object.entries(fields).forEach(([from,to]) => { if (values[from] !== undefined) changes[to] = values[from]; });
    // Copy only affected branches; stable target IDs are re-resolved on every save.
    function setPath(object, path, updater) {
      const [key,...rest] = path;
      const copy = Array.isArray(object) ? [...object] : { ...object };
      copy[key] = rest.length ? setPath(object[key], rest, updater) : updater(object[key]);
      return copy;
    }
    let result = next;
    target.refs.forEach(ref => {
      result = setPath(result, ref.path, row => ({ ...row, ...changes,
        ...(values.country !== undefined && ref.path.includes('legalArrangements') ? { domicile: values.country } : {}),
        ...(values.dateOfBirth !== undefined && Object.hasOwn(row,'dob') ? { dob: values.dateOfBirth } : {}) }));
    });
    next.ownership = syncLinkedSignatories(result.ownership);
  }
  // Keep the visual ownership builder consistent with the edited party.
  const treeId = target.id === 'customer' ? 'X0' : target.nodeId;
  if (treeId && form.ownership?.assistantTree?.nodes) {
    next.ownership = { ...(next.ownership || form.ownership), assistantTree: { ...form.ownership.assistantTree,
      nodes: (next.ownership || form.ownership).assistantTree.nodes.map(node => String(node.ID || node.id) === treeId ? { ...node,
        ...(values.clientName !== undefined ? { name: values.clientName } : {}),
        ...(values.dateOfBirth !== undefined ? { dateOfBirth: values.dateOfBirth, dob: values.dateOfBirth } : {}),
        ...(values.nationality !== undefined ? { nationality: values.nationality } : {}),
        ...(values.country !== undefined ? { countryOfResidence: values.country } : {}),
        ...(values.address !== undefined ? { address: values.address } : {}) } : node) } };
  }
  return next;
}
